// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useCallback, useMemo } from 'react';

import {
  type WorkOrder,
  type WorkOrderItem,
  useEvolvePatch,
  type UpdateWorkOrderItemBody,
  useEvolveGet,
} from '@evolvemep/api-ts';
import { Card, Flex, Stack, Text, Divider } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { AttachmentsPopover } from 'components/Mantine/Attachments/AttachmentsPopover';
import { EntityStatusBadge } from 'components/Mantine/EntityStatusBadge';
import { EntityStatusPoint } from 'components/Mantine/EntityStatusPoint';
import { isNotNil, isNil } from 'helpers/isNotNil';
import { useIsMobile } from 'hooks/useIsMobile';
import { BadgeButton } from 'modules/Shop/WorkOrders/WorkOrderDetail/BadgeButton';
import { isReleased, getTaskStatusFromItem } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import { EditItemMenu } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/EditItemMenu';
import { EditQtyMfg } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/EditQtyMfg';
import { ShipmentAllocationBadge } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/ShipmentAllocationBadge';
import type {
  WorkOrderItemModalProps,
  WorkOrderItemModalTab,
} from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/WorkOrderItemModal';
import { WorkOrderItemTypeIcon } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/WorkOrderItemTypeIcon';
import { useWorkOrderDetailTrackedModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/modalRegistries';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';

type Props = {
  item: WorkOrderItem;
  workOrder: WorkOrder;
  hideDetails?: boolean;
  selected?: boolean;
  onSelectWorkOrderItem?: (workOrderItem: WorkOrderItem | null) => void;
  selectedModalTab?: WorkOrderItemModalTab;
  onModalTabChange?: (tab: WorkOrderItemModalTab) => void;
  onTaskAttachmentsChanged?: () => void;
} & (
  | {
      onDeleteWorkOrderItem: (workOrderItem: WorkOrderItem) => void;
      onMultipleItemsUpdated: () => void;
      onItemUpdated: (updatedItem: WorkOrderItem, originalItem: WorkOrderItem) => void;
      /** @defaultValue true */
      editable?: boolean;
    }
  | {
      onDeleteWorkOrderItem?: (workOrderItem: WorkOrderItem) => void;
      onMultipleItemsUpdated?: () => void;
      onItemUpdated?: (updatedItem: WorkOrderItem, originalItem?: WorkOrderItem) => void;
      /** @defaultValue true */
      editable: false;
    }
) &
  Pick<WorkOrderItemModalProps, 'navProps'>;

export const EditableWorkOrderItem = ({
  item: initialItem,
  workOrder,
  onItemUpdated: onItemUpdatedParent,
  onDeleteWorkOrderItem,
  editable: editableParent = true,
  hideDetails,
  selected,
  onSelectWorkOrderItem,
  navProps,
  onMultipleItemsUpdated,
  selectedModalTab,
  onModalTabChange,
  onTaskAttachmentsChanged,
}: Props) => {
  const isMobile = useIsMobile();
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);
  const [item, setItem] = useState(initialItem);

  const onItemUpdated = useCallback(
    (updatedItem: WorkOrderItem) => {
      onItemUpdatedParent?.(updatedItem, item);
      setItem(updatedItem);
    },
    [item, onItemUpdatedParent],
  );

  const { apiCall: refreshWorkOrderItemApiCall, loading: refreshing } = useEvolveGet(
    `shop/workOrder/item/${item.workOrderItemId}`,
  );
  const refreshWorkOrderItem = useCallback(
    async () => refreshWorkOrderItemApiCall().then(onItemUpdated),
    [onItemUpdated, refreshWorkOrderItemApiCall],
  );

  const { apiCall: patchWorkOrderItemApiCall, loading: saving } = useEvolvePatch(
    `shop/workOrder/item/${item.workOrderItemId}`,
  );

  const updateWorkOrderItem = useCallback(
    async (body: UpdateWorkOrderItemBody) => patchWorkOrderItemApiCall(body).then(onItemUpdated),
    [onItemUpdated, patchWorkOrderItemApiCall],
  );

  const editable = editableParent && canEditWorkOrder('editItems') && !saving;

  const { hasNext, hasPrev, onNext, onPrev } = navProps ?? {};
  // The open modal's props are rebuilt whenever this changes, so it carries every live value the modal shows
  const workOrderItemModalProps = useMemo(
    () => ({
      workOrder,
      workOrderItem: item,
      updateWorkOrderItem,
      onDeleteWorkOrderItem,
      navProps:
        isNotNil(onNext) && isNotNil(onPrev) ? { hasNext: !!hasNext, hasPrev: !!hasPrev, onNext, onPrev } : undefined,
      refreshWorkOrderItem,
      refreshing,
      onItemsAdded: onMultipleItemsUpdated,
      selectedModalTab,
      onModalTabChange,
      onTaskAttachmentsChanged,
    }),
    [
      workOrder,
      item,
      updateWorkOrderItem,
      onDeleteWorkOrderItem,
      hasNext,
      hasPrev,
      onNext,
      onPrev,
      refreshWorkOrderItem,
      refreshing,
      onMultipleItemsUpdated,
      selectedModalTab,
      onModalTabChange,
      onTaskAttachmentsChanged,
    ],
  );
  useWorkOrderDetailTrackedModal('workOrderItemModal', {
    variable: selected === true ? workOrderItemModalProps : null,
    props: (openedModalProps) => openedModalProps,
    onClose: () => onSelectWorkOrderItem?.(null),
  });

  const parentIsReleased = isReleased(workOrder);

  return (
    <Flex>
      <Card
        key={item.workOrderItemId}
        bg="gray.0"
        p="xs"
        radius="sm"
        onClick={() => !hideDetails && onSelectWorkOrderItem?.(item)}
        className={!hideDetails && isNotNil(onSelectWorkOrderItem) ? 'editable-work-order-item' : undefined}
        flex="1 1 auto"
      >
        <Flex align="center" justify="space-between" gap={isMobile ? 'xs' : 'md'} wrap="wrap">
          <Flex align="center" gap="sm">
            <WorkOrderItemTypeIcon item={item} />
            <Stack gap={0}>
              <Text lineClamp={2}>{item.name}</Text>
              <Flex>
                <EditQtyMfg item={item} updateItem={updateWorkOrderItem} editable={editable && !isMobile} />
              </Flex>
            </Stack>
          </Flex>

          {isMobile && <Divider />}

          <Flex gap="xs" flex="1 0 auto" justify="flex-end" align="center">
            {!hideDetails && (
              <Flex c="dimmed" gap="xs" align="center">
                <AttachmentsPopover
                  size="xl"
                  workOrderItemId={item.workOrderItemId}
                  numberOfAttachments={item.attachmentCount}
                  allowUploads="workOrderItem"
                  showWhenEmpty
                  onFilesUploaded={refreshWorkOrderItem}
                  onWebLinkAdded={refreshWorkOrderItem}
                  onAttachmentDeleted={refreshWorkOrderItem}
                />

                {item.billOfMaterialsItemCount === 0 && !parentIsReleased && (
                  <AgGridStyleTooltip label="This item has no bill of materials." withArrow openOnTap>
                    {isMobile ? (
                      <EvolveIcon icon="MissingBom" color="orange.7" size="sm" />
                    ) : (
                      <BadgeButton
                        size="xl"
                        variant="outline"
                        color="orange.7"
                        stopPropagation={isNil(onModalTabChange)}
                        onClick={() => onModalTabChange?.('bom')}
                      >
                        <EvolveIcon icon="MissingBom" />
                      </BadgeButton>
                    )}
                  </AgGridStyleTooltip>
                )}

                {!item.hasShopTask && canEditWorkOrder('editTasks') && (
                  <AgGridStyleTooltip
                    label={
                      item.hasFabricationElements
                        ? 'This item has no Shop tasks.'
                        : 'Every item on a work order must have at least one task.'
                    }
                    withArrow
                    openOnTap
                  >
                    {isMobile ? (
                      <EvolveIcon icon="TaskList" color={item.hasFabricationElements ? 'orange.7' : 'red'} size="sm" />
                    ) : (
                      <BadgeButton
                        size="xl"
                        color={item.hasFabricationElements ? 'orange.7' : 'red'}
                        variant="outline"
                        leftSection={!item.hasFabricationElements && <EvolveIcon icon="Danger" />}
                        stopPropagation={isNil(onModalTabChange)}
                        onClick={() => onModalTabChange?.('tasks')}
                      >
                        {item.hasFabricationElements ? <EvolveIcon icon="TaskList" /> : 'No Tasks'}
                      </BadgeButton>
                    )}
                  </AgGridStyleTooltip>
                )}

                <ShipmentAllocationBadge
                  item={item}
                  onClick={() => onModalTabChange?.('shipments')}
                  stopPropagation={isNil(onModalTabChange)}
                />

                {parentIsReleased &&
                  (isMobile ? (
                    <EntityStatusPoint customStatusEntityName="Task" stateName={getTaskStatusFromItem(item)} />
                  ) : (
                    <EntityStatusBadge
                      customStatusEntityName="Task"
                      stateName={getTaskStatusFromItem(item)}
                      size="xl"
                    />
                  ))}
              </Flex>
            )}

            <EditItemMenu
              item={item}
              workOrder={workOrder}
              editable={editable}
              updateItem={updateWorkOrderItem}
              refreshItem={refreshWorkOrderItem}
              onMultipleItemsUpdated={onMultipleItemsUpdated}
              onDeleteItem={onDeleteWorkOrderItem}
              onViewDetails={onSelectWorkOrderItem ? () => onSelectWorkOrderItem(item) : undefined}
              loadingOverride={refreshing}
            />
          </Flex>
        </Flex>
      </Card>
    </Flex>
  );
};
